export const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';
import { Context, useContext } from 'solid-js';

export function uuidv4() {
    // crypto is only available in a secure context, we fallback to Math.random if it's not available
    if (crypto && crypto.randomUUID) {
        return crypto.randomUUID();
    }

    const rand = (c: number) => (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4);
    return ('00000000-0000-4000-0000-000000000000').replace(/[0]/g, function (c) {
        return rand(Number(c)).toString(16).toUpperCase();
    });
}

export function delay(ms: number) : Promise<void> {
    return new Promise((res, err) => {
        setTimeout(() => {
            res();
        }, ms)
    });
}

/**
 * Allows exporting internal functionality to the legacy ST object.
 * @param data A dictionary of values to merge into window.ST
 */
export function exportForST(data: Record<string, unknown>) {
    const st = (window as any).ST ?? {};
    (window as any).ST = deepMerge(st, data);
}

export function deepMerge(target: any, source: any) {
    for (const key in source) {
        if (source[key] instanceof Object && key in target) {
            Object.assign(source[key], deepMerge(target[key], source[key]))
        }
    }
    Object.assign(target || {}, source)
    return target
}

export function requireContext<T>(context: Context<T|undefined>): T {
    const res = useContext(context);
    if (!res) {
        throw new Error('Context not found');
    }
    return res;
}


type DebouncedFunction<F extends (...args: any[]) => any> = ((...args: Parameters<F>) => void) & { abort: () => void };

export function debounce<F extends (...args: any[]) => any>(func: F, delay: number): DebouncedFunction<F> {
    let timeout: number | null = null;

    function abort() {
        if (timeout !== null) {
            clearTimeout(timeout);
        }
    }

    const res = (...args: Parameters<F>) => {
        const later = () => {
            timeout = null;
            func(...args);
        };

        abort();
        timeout = window.setTimeout(later, delay);
    };

    res.abort = abort;

    return res as DebouncedFunction<F>;
}
